import React, { ReactElement, useState } from 'react';
import { Button, Divider, Input, Select, Form, Skeleton, SelectProps } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { NamePath } from 'antd/lib/form/interface';
import { I18n } from '@aws-amplify/core';

import { useApplicationContext } from '@/context/Application';
import { SectorDTO } from '@/components/ui/Inputs/types/response/sector';
import { useCreateSector } from '@/hooks/v1/useSectorMutations';

import { CreateSectorRequest } from '../types/request/sector';

const { useFormInstance } = Form;

type SectorSelectProps = {
	size?: SizeType;
	loading: boolean;
	disabled: boolean;
	sectorId?: string;
	fieldName?: NamePath;
	allowCreation?: boolean;
	handleClearSector?: () => void;
	sectors: SectorDTO[] | undefined;
	handleSelectSector?: (id: string) => void;
} & SelectProps;

type OptionType = { label: string; value: string } | undefined;

export function SectorSelect({
	sectors,
	loading,
	disabled,
	sectorId,
	size = 'middle',
	handleClearSector,
	handleSelectSector,
	allowCreation = true,
	fieldName = 'sector_id'
}: Readonly<SectorSelectProps>) {
	const [sectorName, setSectorName] = useState('');

	const { company, organization } = useApplicationContext();
	const { validateFields, setFieldValue } = useFormInstance();

	const { mutateAsync: createSector, isLoading } = useCreateSector();

	async function onCreateSector() {
		const body: CreateSectorRequest = {
			organization_id: organization.id,
			company_id: company.id,
			name: sectorName
		};

		const { data: sector } = await createSector({ body });
		const sectorId = sector?.data?.id;
		if (sectorId && fieldName) {
			setFieldValue(fieldName, sectorId);
			setFieldValue('line_id', undefined);
			setFieldValue(['file', 'line_id'], undefined);
			setFieldValue('workstation_id', undefined);
			setFieldValue(['file', 'workstation_id'], undefined);
		}
		setSectorName('');
	}

	async function handleChange(id: string) {
		if (handleSelectSector) {
			handleSelectSector(id);
			await validateFields([fieldName]);
		}
	}

	function handleClear() {
		if (handleClearSector) {
			handleClearSector();
		}
	}

	function handleSectorNameChange(name: string) {
		setSectorName(name);
	}

	function filterOption(option: OptionType, input: string): boolean {
		return (option?.label.toLowerCase() ?? '').includes(input.toLowerCase());
	}

	function showDropdownrender(menu: ReactElement): JSX.Element {
		if (!allowCreation) {
			return <div>{menu}</div>;
		}
		return (
			<div>
				{menu}
				<Divider style={{ margin: '4px 0' }} />
				<div
					style={{
						display: 'flex',
						flexWrap: 'nowrap',
						padding: 8
					}}
				>
					<Input
						style={{ flex: 'auto', borderRadius: '7px' }}
						value={sectorName}
						onChange={(e) => handleSectorNameChange(e.target.value)}
					/>
					<Button
						onClick={onCreateSector}
						type="link"
						loading={isLoading}
						disabled={!sectorName || sectorName.length < 3}
					>
						{I18n.get('Add item')}
					</Button>
				</div>
			</div>
		);
	}

	if (loading) {
		return <Skeleton.Input block active size="small" />;
	}

	return (
		<Select
			allowClear
			showSearch
			size={size}
			value={sectorId}
			loading={loading}
			disabled={disabled}
			onClear={handleClear}
			onChange={handleChange}
			placeholder={I18n.get('Sector')}
			dropdownRender={(menu) => showDropdownrender(menu)}
			filterOption={(input, option) => filterOption(option, input)}
			options={sectors?.map(({ id, name }) => ({ label: name, value: id }))}
		/>
	);
}
